/**
 * Which Bible direction suits THIS shot.
 *
 * The Bible mixes two kinds of statement and, until now, applied both to everything. World
 * facts ("verdigris copper", "barnacle crust") are true in every shot. Framing facts
 * ("low horizon, sky dominant", "35mm at chest height") describe how you shoot an
 * establishing wide, and are simply wrong on a macro. Measured: "close up of hands holding a
 * brass key" received `low horizon, sky dominant` and `35mm at chest height`.
 *
 * Everything here is a CLOSED VOCABULARY and a deterministic match. No classifier, no model
 * call, no free-text interpretation. The repo already learned that lesson once: injecting
 * Bible pins into every shot "was tried and reverted the same day"
 * (`visual-inputs/planVisualInputs.js`), and the fix was a deterministic name match, not a
 * smarter guess. Same discipline here.
 */

import { CAMERA_FRAMING_OPTIONS } from '../../config/directorPresets/cameraSlotOptions';

/** This service owns which authored framing ids suppress Bible direction. */
export const CLOSE_BAND = Object.freeze([
  'Extreme Close-Up',
  'Close-Up',
  'Medium Close-Up',
  'Insert',
]);

export const WIDE_BAND = Object.freeze(
  CAMERA_FRAMING_OPTIONS.filter(({ id }) => !CLOSE_BAND.includes(id)).map(({ id }) => id),
);

export const CAMERA_FRAMING_LABELS = Object.freeze(CAMERA_FRAMING_OPTIONS.map(({ label }) => label));

const CLOSE_BAND_SET = new Set(CLOSE_BAND.map((label) => label.toLowerCase()));
const LABELS_BY_LOWER = new Map(CAMERA_FRAMING_LABELS.map((label) => [label.toLowerCase(), label]));

/**
 * Phrases a person types when they mean a close shot, mapped to the authored label.
 *
 * Longest first, so "extreme close-up" is not consumed by "close-up". Only the close band
 * needs synonyms: nothing is suppressed on the wide band, so a missed wide costs nothing,
 * while a missed close costs a macro shot full of establishing-wide direction.
 */
const PROMPT_SYNONYMS = Object.freeze([
  ['extreme close-up', 'Extreme Close-Up'],
  ['extreme closeup', 'Extreme Close-Up'],
  ['extreme close up', 'Extreme Close-Up'],
  ['medium close-up', 'Medium Close-Up'],
  ['medium closeup', 'Medium Close-Up'],
  ['medium close up', 'Medium Close-Up'],
  ['close-up', 'Close-Up'],
  ['closeup', 'Close-Up'],
  ['close up', 'Close-Up'],
  ['macro', 'Extreme Close-Up'],
  ['insert shot', 'Insert'],
  ['extreme wide', 'Extreme Wide'],
  ['wide shot', 'Wide Shot'],
  ['establishing shot', 'Wide Shot'],
  ['establishing', 'Wide Shot'],
  ['medium wide', 'Medium Wide'],
  ['medium shot', 'Medium Shot'],
  ['over the shoulder', 'Over the Shoulder'],
  ['two shot', 'Two Shot'],
]);

function escapeForRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Whole-phrase match only, with the same "not a word character" anchoring the pin name-match
 * uses. Without it "enclose upstream" would read as a close-up.
 */
function containsPhrase(haystack, phrase) {
  return new RegExp(`(^|[^a-z0-9])${escapeForRegex(phrase)}([^a-z0-9]|$)`, 'i').test(haystack);
}

/**
 * The leading `[...]` tag of a compiled shot prompt -> its framing label, or null.
 *
 * This reads back our OWN compiler's output (`shots/shotPromptParts.js` →
 * `renderCompiledShotPrompt`), so on a storyboard nothing is being inferred: the user
 * authored the framing and we are recovering it.
 *
 * The first entry is not always a framing label. The repo's own compile goldens contain
 * `[Slow 360 Orbital]`, a camera MOVEMENT with no framing at all, so an unrecognised entry
 * must return null rather than be treated as a framing.
 */
export function parseShotFramingTag(prompt) {
  if (typeof prompt !== 'string') return null;
  const match = /^\s*\[([^\]]*)\]/.exec(prompt);
  if (!match) return null;
  for (const entry of match[1].split(',')) {
    const label = LABELS_BY_LOWER.get(entry.trim().toLowerCase());
    if (label) return label;
  }
  return null;
}

/**
 * A framing stated in the user's own prompt text, or null.
 *
 * Only used where nothing authored the shot, which today means the Create panel.
 */
export function detectPromptFraming(prompt) {
  if (typeof prompt !== 'string' || !prompt.trim()) return null;
  const haystack = prompt.toLowerCase();
  for (const [phrase, label] of PROMPT_SYNONYMS) {
    if (containsPhrase(haystack, phrase)) return label;
  }
  return null;
}

export function isCloseBandFraming(framing) {
  return typeof framing === 'string' && CLOSE_BAND_SET.has(framing.toLowerCase());
}

/**
 * The one decision owner. Anything that needs to know whether framing direction applies calls
 * this, so a display can never disagree with what was actually composed.
 *
 * Two rulings are load-bearing:
 *
 * UNKNOWN INCLUDES. No stated framing means every clause still applies. A wrong frame is
 * visible in the result and one click to redo; a look that quietly went missing is neither.
 *
 * ONLY THE CLOSE BAND SUPPRESSES. "Any stated framing suppresses" would strip framing
 * direction from every storyboard frame, since storyboard shots almost always state one --
 * which would be the invisible-omission failure wearing a different hat.
 */
export function resolveFramingScope({ shotFraming = null, basePrompt = '', relevanceMode = 'auto' } = {}) {
  const effectiveFraming = shotFraming || detectPromptFraming(basePrompt);
  return {
    effectiveFraming,
    suppressFramingClauses: relevanceMode !== 'full' && isCloseBandFraming(effectiveFraming),
  };
}

export default resolveFramingScope;
